import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, Input, message, Switch, Alert } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatGapDeliveries, useGetMatWarehouses } from '../../../hooks/genericHooks';
import type { MatGapDeliveries, MatWarehouses } from '../../../types';
import { insertData } from '../../../services/api';
import { useAuth } from '../../../components/common/security/AuthContext';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

export default function MatGapTxnIncoming() {
  const navigate = useNavigate();
  const { data: delData = [], isLoading } = useGetMatGapDeliveries() as any;
  const { data: whData = [] } = useGetMatWarehouses() as any;

  const deliveries: MatGapDeliveries[] = Array.isArray(delData) ? delData : (delData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const { user: currentUser } = useAuth();

  // Owned warehouses set for current user
  const ownedWhSet = useMemo(() => {
    const s = new Set<number>();
    (warehouses || []).forEach(w => {
      const owners: string[] = Array.isArray(w?.owners) ? (w.owners as string[]) : [];
      if (currentUser && owners.includes(String(currentUser))) s.add(Number(w.id));
    });
    return s;
  }, [warehouses, currentUser]);

  const whOptions = useMemo(() => (warehouses || []).map(w => ({ value: w.id, label: w.name, disabled: !ownedWhSet.has(Number(w.id)) })), [warehouses, ownedWhSet]);
  const hasAnyOwned = useMemo(() => ownedWhSet.size > 0, [ownedWhSet]);

  // If currently selected warehouse is not owned, clear it
  React.useEffect(() => {
    if (warehouseId != null && !ownedWhSet.has(Number(warehouseId))) {
      setWarehouseId(undefined);
    }
  }, [ownedWhSet]);

  // Controls
  const [warehouseId, setWarehouseId] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState<string>('');
  const [shipmentFilter, setShipmentFilter] = useState<string>('');
  const [date, setDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [useDeliveryDate, setUseDeliveryDate] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const isOwner = useMemo(() => {
    if (!warehouseId || !currentUser) return false;
    const wh = (warehouses || []).find(w => Number(w.id) === Number(warehouseId));
    const owners: string[] = Array.isArray(wh?.owners) ? (wh!.owners as string[]) : [];
    return owners.includes(String(currentUser));
  }, [warehouseId, currentUser, warehouses]);
  const canTransact = Boolean(warehouseId && currentUser && isOwner);

  // Selection
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<MatGapDeliveries[]>([]);

  const filtered = useMemo(() => {
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const ship = shipmentFilter.trim();
    const [start, end] = dateRange;
    return (deliveries || []).filter(d => {
      // Material/description search supports multi-token AND and multiple quoted phrases
      if (tokens.length > 0 || phraseMatches.length > 0) {
        const hay = [
          String(d.mat_id || ''),
          d.mat_desc || '',
          d.unit || '',
          d.mat_group || '',
          d.mat_group_long || '',
          d.hierarchy || '',
          d.hierarchy_long || '',
          d.sat_no || '',
          d.sas_no || '',
          String(d.shipment_id || ''),
        ].join(' ').toLowerCase();
        if (phraseMatches.length > 0) {
          if (!phraseMatches.every(p => hay.includes(p))) return false;
        } else if (!tokens.every(t => hay.includes(t))) {
          return false;
        }
      }
      if (ship && String(d.shipment_id || '').indexOf(ship) === -1) return false;
      // delivery_date range (inclusive) only when using delivery date
      if (useDeliveryDate && (start || end)) {
        const dv = d.delivery_date ? new Date(d.delivery_date) : null;
        if (!dv) return false;
        if (start) {
          const ds = new Date(start);
          if (dv < new Date(ds.getFullYear(), ds.getMonth(), ds.getDate())) return false;
        }
        if (end) {
          const de = new Date(end);
          if (dv > new Date(de.getFullYear(), de.getMonth(), de.getDate(), 23,59,59,999)) return false;
        }
      } else if (!useDeliveryDate && date) {
        // when not using delivery date, still allow filtering by a single day on delivery_date
        const dv = d.delivery_date ? new Date(d.delivery_date) : null;
        if (!dv) return false;
        const ds = new Date(date);
        const startOfDay = new Date(ds.getFullYear(), ds.getMonth(), ds.getDate());
        const endOfDay = new Date(ds.getFullYear(), ds.getMonth(), ds.getDate(), 23,59,59,999);
        if (dv < startOfDay || dv > endOfDay) return false;
      }
      return true;
    });
  }, [deliveries, search, shipmentFilter, dateRange, date, useDeliveryDate]);

  const columns: any[] = useMemo(() => [
    { title: 'Shipment', dataIndex: 'shipment_id', key: 'shipment_id', width: 110, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => String(a.shipment_id || '').localeCompare(String(b.shipment_id || '')) },
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 130, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => String(a.mat_id || '').localeCompare(String(b.mat_id || '')) },
    { title: 'Description', dataIndex: 'mat_desc', key: 'mat_desc', ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Group (Long)', dataIndex: 'mat_group_long', key: 'mat_group_long', width: 220, ellipsis: true, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.mat_group_long || '').localeCompare(b.mat_group_long || '') },
    { title: 'Hierarchy (Long)', dataIndex: 'hierarchy_long', key: 'hierarchy_long', width: 220, ellipsis: true, sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.hierarchy_long || '').localeCompare(b.hierarchy_long || '') },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 100, align: 'right', sorter: (a: MatGapDeliveries, b: MatGapDeliveries) => (a.qty ?? 0) - (b.qty ?? 0) },
    { title: 'Delivery Date', dataIndex: 'delivery_date', key: 'delivery_date', width: 140,
      render: (v: string) => v ? new Date(v).toLocaleDateString() : '-',
      exportValue: (r: MatGapDeliveries) => r.delivery_date ? dayjs(r.delivery_date).format('YYYY-MM-DD') : ''
    },
    { title: 'SAT', dataIndex: 'sat_no', key: 'sat_no', width: 120 },
    { title: 'SAS', dataIndex: 'sas_no', key: 'sas_no', width: 120 },
  ], []);

  const selectedCount = useMemo(() => selectedRows.length, [selectedRows]);

  const handleSubmit = async () => {
    try {
      if (!warehouseId) { message.warning('Please select a destination warehouse'); return; }
      if (!canTransact) { message.error('You are not an owner of the selected warehouse. Incoming transactions are restricted.'); return; }
      if (selectedRows.length === 0) { message.warning('Please select at least one delivery'); return; }
      setSubmitting(true);

      const payloads = selectedRows.map(r => {
        // Determine txn_date: prefer delivery_date when toggle is ON and available; otherwise use selected date; if neither, omit to use DB default
        const pickedDate = (useDeliveryDate && r.delivery_date)
          ? r.delivery_date
          : (date || undefined);

        const base: any = {
          op_type: 'incoming',
          mat_id: r.mat_id,
          qty: r.qty,
          unit: r.unit,
          to_warehouse_id: warehouseId,
          reference_no: `DEL#${r.id}`,
        };
        if (pickedDate) base.txn_date = pickedDate; // let DB default apply if not provided
        if ((r as any).shipment_id != null) base.shipment_id = (r as any).shipment_id;
        if (r.sat_no != null) base.sat_no = r.sat_no;
        if (r.sas_no != null) base.sas_no = r.sas_no;
        if (r.mat_desc) base.notes = r.mat_desc;
        return base;
      });

      // Batch insert in a single request (backend supports array payloads)
      await insertData('mat_gap_txns', payloads);
      message.success(`Created ${payloads.length} incoming transaction(s)`);
      setSelectedKeys([]);
      setSelectedRows([]);
    } catch (err) {
      console.error(err);
      message.error('Failed to create transactions');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Incoming from Deliveries</Title>
              <Text type="secondary">Create mat_gap_txns(op_type='incoming') from selected deliveries</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <DatePicker.RangePicker
              onChange={(vals) => {
                if (!vals || vals.length !== 2) { setDateRange([null, null]); return; }
                setDateRange([
                  vals[0] ? vals[0].format('YYYY-MM-DD') : null,
                  vals[1] ? vals[1].format('YYYY-MM-DD') : null,
                ]);
              }}
              disabled={!useDeliveryDate}
              disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
            />
            <Select
              placeholder="Destination Warehouse"
              allowClear
              value={warehouseId}
              onChange={(v) => setWarehouseId(v)}
              options={whOptions}
              style={{ width: 260 }}
            />
            <Input
              placeholder="Search material/desc/SAT/SAS..."
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
            />
            <Input
              placeholder="Shipment ID"
              allowClear
              value={shipmentFilter}
              onChange={(e) => setShipmentFilter(e.target.value)}
              style={{ width: 160 }}
            />
            <DatePicker
              allowClear
              value={date ? dayjs(date) : null}
              onChange={(d) => setDate(d ? d.format('YYYY-MM-DD') : null)}
              disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
            />
            <Button
              action="add"
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!hasAnyOwned}
            >
              Submit Incoming{selectedCount ? ` (${selectedCount})` : ''}
            </Button>
          </Space>
        </Col>
      </Row>
      <StandardTable<MatGapDeliveries>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => String(r.id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys, rows) => { setSelectedKeys(keys); setSelectedRows(rows as MatGapDeliveries[]); },
          preserveSelectedRowKeys: true,
        }}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
